import { Link } from 'react-router-dom';
import { Heart, BarChart3, ArrowRight, Star, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';

const typeColors = {
  Travel: { bg: '#EEF2FF', text: '#3B5BDB', border: '#C5CAE9' },
  Cashback: { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
  Rewards: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
  Premium: { bg: '#F5F3FF', text: '#7C3AED', border: '#DDD6FE' },
  Islamic: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
};

export default function CardItem({ card, onToggleFavorite, onToggleCompare, isFavorite, isCompare, featured }) {
  const tc = typeColors[card.card_type] || { bg: '#F4F4F5', text: '#52525B', border: '#E4E4E7' };

  return (
    <div
      data-testid={`card-item-${card.id}`}
      className={`group bg-white rounded-xl overflow-hidden card-hover ${featured ? 'border-2 border-[#002FA7]/20 shadow-md' : 'border border-[#E4E4E7]'}`}
    >
      {/* Credit card visual header */}
      <div
        className="p-5 pb-4 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${card.bank_color}ee, ${card.bank_color}99)` }}
      >
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/10 -translate-y-8 translate-x-8" />
        <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full bg-white/5 translate-y-6 -translate-x-6" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-bold text-white/90 uppercase tracking-widest">{card.bank_name}</span>
            <Wifi className="w-4 h-4 text-white/60 rotate-90" />
          </div>
          <h3 className="font-heading font-bold text-white text-base leading-snug mb-2">{card.card_name}</h3>
          <div className="font-mono text-white/70 text-[11px] tracking-[0.2em]">
            ****&nbsp;&nbsp;****&nbsp;&nbsp;****&nbsp;&nbsp;{Math.floor(1000 + Math.random() * 9000)}
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Type badge + Islamic tag */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
            style={{ backgroundColor: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}
          >
            {card.card_type}
          </span>
          {card.is_islamic && card.card_type !== 'Islamic' && (
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]">
              Islamic
            </span>
          )}
          {featured && (
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] flex items-center gap-1">
              <Star className="w-2.5 h-2.5 fill-current" /> Top Pick
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-3">
          <div>
            <span className="text-[10px] text-[#71717A] font-medium uppercase tracking-wider block">Annual Fee</span>
            <span className="number-display font-semibold text-[15px] text-[#09090B]">
              {card.annual_fee === 0 ? <span className="text-[#059669]">FREE</span> : `AED ${card.annual_fee.toLocaleString()}`}
            </span>
          </div>
          <div>
            <span className="text-[10px] text-[#71717A] font-medium uppercase tracking-wider block">Cashback</span>
            <span className="number-display font-semibold text-[15px] text-[#09090B]">
              {card.cashback_rate > 0 ? <span className="text-[#059669]">Up to {card.cashback_rate}%</span> : '\u2014'}
            </span>
          </div>
          <div>
            <span className="text-[10px] text-[#71717A] font-medium uppercase tracking-wider block">Min Salary</span>
            <span className="number-display text-[13px] text-[#52525B]">AED {card.min_salary.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-[10px] text-[#71717A] font-medium uppercase tracking-wider block">Interest</span>
            <span className="number-display text-[13px] text-[#52525B]">
              {card.interest_rate_monthly > 0 ? `${card.interest_rate_monthly}%/mo` : 'N/A'}
            </span>
          </div>
        </div>

        {/* Welcome bonus */}
        {card.welcome_bonus && (
          <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2 mb-3">
            <span className="text-[10px] font-bold text-[#92400E] uppercase tracking-wider">Welcome Bonus</span>
            <p className="text-[12px] text-[#78350F] mt-0.5 leading-snug">{card.welcome_bonus}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1.5 pt-3 border-t border-[#F4F4F5]">
          <Button
            variant="ghost"
            size="sm"
            data-testid={`favorite-btn-${card.id}`}
            onClick={() => onToggleFavorite(card.id)}
            className={`h-8 w-8 p-0 rounded-full ${isFavorite ? 'text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-600' : 'text-[#A1A1AA] hover:text-red-500 hover:bg-red-50'}`}
          >
            <Heart className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid={`compare-btn-${card.id}`}
            onClick={() => onToggleCompare(card.id)}
            className={`h-8 w-8 p-0 rounded-full ${isCompare ? 'text-[#002FA7] bg-[#EEF2FF] hover:bg-[#DBEAFE]' : 'text-[#A1A1AA] hover:text-[#002FA7] hover:bg-[#EEF2FF]'}`}
          >
            <BarChart3 className={`w-3.5 h-3.5`} />
          </Button>
          <Link to={`/cards/${card.id}`} className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              data-testid={`detail-btn-${card.id}`}
              className="h-8 text-[11px] font-semibold rounded-full px-4 border-[#E4E4E7] text-[#09090B] hover:border-[#002FA7] hover:text-[#002FA7] gap-1"
            >
              Learn More <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
